import React from 'react';
import { novaThemes, novaSignal, NovaThemeName } from './novaTheme';

interface NovaToastProps {
  message: string;
  theme: NovaThemeName;
  className?: string;
  /** 'error' swaps the green tick for a red alert mark — used for a failed export, since
   * showing a success tick over a failure message would be actively misleading. */
  variant?: 'success' | 'error';
}

// No error colour exists in novaTheme yet (its one accent, g4, is explicitly a
// gradient-only stop, not a semantic error colour) — kept local to this one
// error state rather than introducing a new shared token for a single caller.
const NOVA_TOAST_ERROR_COLOR = '#D64545';

// The buildout's "Copy → toast" spec: a quiet in-app confirmation, never a browser
// alert — dark Ink pill, green tick. Auto-dismiss is the caller's responsibility
// (see NovaAnswerCard/NovaMessage's copy/export handlers), this just renders the pill.
const NovaToast: React.FC<NovaToastProps> = ({ message, theme, className, variant = 'success' }) => {
  const surfaces = novaThemes[theme];
  const isError = variant === 'error';

  return (
    <div
      role="status"
      className={`pointer-events-none inline-flex items-center gap-[11px] rounded-full py-3 pr-5 pl-3.5 text-[14.5px] font-medium shadow-[0_18px_40px_-16px_rgba(30,28,22,0.5)] ${className ?? ''}`}
      style={{ background: surfaces.surfaces.userBubble, color: surfaces.ink.userBubbleText }}
    >
      <span
        className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full"
        style={{ background: isError ? NOVA_TOAST_ERROR_COLOR : novaSignal }}
      >
        {isError ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 8v5M12 16.5h.01" />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        )}
      </span>
      {message}
    </div>
  );
};

export default NovaToast;
